// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
import { ArrowClockwise, CheckCircle, XCircle } from "@phosphor-icons/react";
import { Button, Flex } from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  AskV2ReviewRejectScopePrompt,
  type ScopeChoice,
} from "./AskV2ReviewRejectScopePrompt";
import { type RejectionScope } from "./rememberedSentence";

type JudgmentTone = "confirm" | "reject" | "restore";

function JudgmentActionButton({
  "data-testid": dataTestId,
  label,
  icon: Icon,
  pressed,
  onClick,
  tone,
  disabled,
}: {
  "data-testid": string;
  label: string;
  icon: typeof CheckCircle;
  pressed: boolean;
  onClick: () => void;
  tone: JudgmentTone;
  disabled?: boolean;
}) {
  const { t } = useTranslation("ui");
  const isConfirm = tone === "confirm";
  const isRestore = tone === "restore";
  const variant = isConfirm && pressed ? "solid" : pressed ? "soft" : "outline";

  return (
    <Button
      type="button"
      variant={variant}
      size="1"
      color={isConfirm ? "green" : "gray"}
      data-testid={dataTestId}
      aria-label={
        pressed
          ? t("assetViewer.removeLabel", { label: label.toLowerCase() })
          : label
      }
      aria-pressed={isRestore ? undefined : pressed}
      onClick={onClick}
      disabled={disabled}
      className="min-w-0 flex-1 basis-0"
    >
      <Flex as="span" gap="1" align="center" justify="center">
        <Icon
          size={14}
          weight={isConfirm && pressed ? "fill" : "regular"}
          aria-hidden="true"
        />
        {label}
      </Flex>
    </Button>
  );
}

export type AskV2ReviewCommentJudgmentActionsProps = {
  commentId: string;
  liked?: boolean | null;
  includeRejected: boolean;
  disabled?: boolean;
  onJudge: (liked: boolean | null, scope?: RejectionScope) => void;
  /** The finding's text, shown back as what Puntt will remember. */
  findingText?: string;
  /** The brand the finding names. When set, no scope question is asked. */
  brand?: string;
  /** Scope options offered when the finding names no brand. */
  scopeChoices?: ScopeChoice[];
};

export function AskV2ReviewCommentJudgmentActions({
  commentId,
  liked = null,
  includeRejected,
  disabled = false,
  onJudge,
  findingText = "",
  brand,
  scopeChoices,
}: AskV2ReviewCommentJudgmentActionsProps) {
  const { t } = useTranslation("ui");
  const isRejected = liked === false;
  const showRestore = isRejected && includeRejected;
  // why Reject opens a step instead of firing: rejecting is the click that
  // TEACHES, and the reviewer never sees what was learned unless it is shown
  // at the moment she decides. Confirm stays one click -- agreeing with a
  // finding changes nothing she needs to check.
  const [confirming, setConfirming] = useState(false);

  if (confirming) {
    return (
      <AskV2ReviewRejectScopePrompt
        commentId={commentId}
        findingText={findingText}
        brand={brand}
        choices={scopeChoices}
        onConfirm={(scope) => {
          setConfirming(false);
          onJudge(false, scope);
        }}
        onCancel={() => setConfirming(false)}
      />
    );
  }

  return (
    <Flex
      gap="2"
      className="mt-3 border-t border-puntt-neutral-gray-5 pt-3"
      data-testid={`ask-v2-review-comment-judgment-${commentId}`}
    >
      {showRestore ? (
        <JudgmentActionButton
          data-testid={`ask-v2-review-comment-restore-${commentId}`}
          label={t("assetViewer.restore")}
          icon={ArrowClockwise}
          pressed={false}
          onClick={() => onJudge(null)}
          tone="restore"
          disabled={disabled}
        />
      ) : (
        <>
          <JudgmentActionButton
            data-testid={`ask-v2-review-comment-confirm-${commentId}`}
            label={t("assetViewer.confirm")}
            icon={CheckCircle}
            pressed={liked === true}
            // why: null clears judgment; false would reject-and-hide via liked !== false
            onClick={() => onJudge(liked === true ? null : true)}
            tone="confirm"
            disabled={disabled}
          />
          <JudgmentActionButton
            data-testid={`ask-v2-review-comment-reject-${commentId}`}
            label={t("assetViewer.reject")}
            icon={XCircle}
            pressed={false}
            onClick={() => setConfirming(true)}
            tone="reject"
            disabled={disabled}
          />
        </>
      )}
    </Flex>
  );
}
